import { useEffect, useRef, useState } from 'react';
import './MembershipTypePricingSection.css';
import IndianRupeeInput from './IndianRupeeInput';

type ValidityUnit = 'months' | 'years';

type MembershipTypePricingSectionProps = {
  monthlyPricePaise: number;
  annualPricePaise?: number;
  validityDays: number;
  unlockCriteria: string;
  onChange: (key: string, value: string | number | boolean) => void;
  errors: Record<string, string>;
};

const deriveUnit = (days: number): ValidityUnit => {
  if (days >= 365 && days % 365 === 0) return 'years';
  return 'months';
};

const deriveCount = (days: number, unit: ValidityUnit): string => {
  if (unit === 'years') return String(Math.round(days / 365));
  return String(Math.round(days / 30));
};

const MAX_VALIDITY_DAYS = 3650;
const MAX_COUNT: Record<ValidityUnit, number> = { years: 10, months: 121 };

const toValidityDays = (unit: ValidityUnit, count: string): number => {
  const num = parseInt(count, 10);
  if (isNaN(num) || num <= 0) return 0;
  return unit === 'years' ? num * 365 : num * 30;
};

const buildPreviewLabel = (unit: ValidityUnit, count: string): string => {
  const num = parseInt(count, 10);
  if (isNaN(num) || num <= 0) return '';
  if (unit === 'years') return `${num} ${num === 1 ? 'year' : 'years'} membership`;
  return `${num} ${num === 1 ? 'month' : 'months'} membership`;
};

const formatRupeesFromPaise = (value: number): string => `₹${(Math.max(value || 0, 0) / 100).toLocaleString('en-IN')}`;

const MembershipTypePricingSection = ({
  monthlyPricePaise,
  annualPricePaise,
  validityDays,
  unlockCriteria,
  onChange,
  errors
}: MembershipTypePricingSectionProps) => {
  const pricePaise = monthlyPricePaise;
  const isFreeTier = (pricePaise || 0) === 0;

  const [unit, setUnit] = useState<ValidityUnit>(() => deriveUnit(validityDays || 30));
  const [count, setCount] = useState<string>(() => {
    const u = deriveUnit(validityDays || 30);
    return deriveCount(validityDays || 30, u);
  });
  const ownValidityChange = useRef(false);

  useEffect(() => {
    if (ownValidityChange.current) {
      ownValidityChange.current = false;
      return;
    }
    const newUnit = deriveUnit(validityDays || 30);
    setUnit(newUnit);
    setCount(deriveCount(validityDays || 30, newUnit));
  }, [validityDays]); // eslint-disable-line react-hooks/exhaustive-deps

  const commitValidity = (nextUnit: ValidityUnit, nextCount: string) => {
    const days = toValidityDays(nextUnit, nextCount);
    if (days > 0) {
      ownValidityChange.current = true;
      onChange('validityDays', days);
    }
  };

  const handleUnitChange = (newUnit: ValidityUnit) => {
    setUnit(newUnit);
    commitValidity(newUnit, count);
  };

  const handleCountChange = (raw: string) => {
    const filtered = raw.replace(/[^0-9]/g, '');
    const num = parseInt(filtered, 10);
    if (!isNaN(num) && num > MAX_COUNT[unit]) {
      const capped = String(MAX_COUNT[unit]);
      setCount(capped);
      commitValidity(unit, capped);
      return;
    }
    setCount(filtered);
    commitValidity(unit, filtered);
  };

  const computedDays = toValidityDays(unit, count);
  const validityCapError = computedDays > MAX_VALIDITY_DAYS
    ? `Validity cannot exceed 10 years (${MAX_VALIDITY_DAYS} days)`
    : null;

  const previewLabel = buildPreviewLabel(unit, count);
  const pricePreviewLabel = isFreeTier
    ? 'Free tier'
    : `${formatRupeesFromPaise(pricePaise)} / ${count} ${unit}`;

  return (
    <section className="appointments-v2-card membership-pricing-card">
      <div className="appointments-v2-card-header membership-pricing-card__header">
        <div>
          <h3>Pricing</h3>
          <p className="membership-pricing-card__intro">
            Set the commercial rules for this membership before customers ever see the card.
          </p>
        </div>
      </div>
      <div className="appointments-v2-card-body membership-pricing-card__body">
        {/* Live price pill */}
        <div className="membership-pricing-card__preview-pill">
          <span className="membership-pricing-card__preview-pill-text">{pricePreviewLabel}</span>
        </div>

        <div className="membership-pricing-card__grid">
          <label className="field membership-pricing-card__field">
            <span className="label membership-pricing-card__label">Monthly price</span>
            <IndianRupeeInput
              valuePaise={monthlyPricePaise}
              onChangePaise={(value) => onChange('monthlyPricePaise', value)}
              placeholder="0"
            />
            <span className="membership-pricing-card__helper">
              {isFreeTier ? 'Free membership — no cost to join' : 'Charged per month. Used as the plan’s base price.'}
            </span>
            {errors.monthlyPricePaise ? (
              <span className="membership-pricing-card__error">{errors.monthlyPricePaise}</span>
            ) : null}
          </label>

          <label className="field membership-pricing-card__field">
            <span className="label membership-pricing-card__label">Annual price (optional)</span>
            <IndianRupeeInput
              valuePaise={annualPricePaise ?? 0}
              onChangePaise={(value) => onChange('annualPricePaise', value)}
              placeholder="0"
            />
            <span className="membership-pricing-card__helper">
              Leave 0 to offer this plan monthly only. Set an annual price to enable the yearly option.
            </span>
            {errors.annualPricePaise ? (
              <span className="membership-pricing-card__error">{errors.annualPricePaise}</span>
            ) : null}
          </label>

          <div className="field membership-pricing-card__field">
            <span className="label membership-pricing-card__label">Validity</span>
            <div className="membership-pricing-card__validity-row">
              <select
                className="input membership-pricing-card__input membership-pricing-card__validity-select"
                value={unit}
                onChange={(e) => handleUnitChange(e.target.value as ValidityUnit)}
              >
                <option value="months">Months</option>
                <option value="years">Years</option>
              </select>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={MAX_COUNT[unit]}
                className="input membership-pricing-card__input membership-pricing-card__validity-count"
                value={count}
                placeholder="1"
                onChange={(e) => handleCountChange(e.target.value)}
              />
            </div>
            {previewLabel ? (
              <span className="membership-pricing-card__validity-preview">{previewLabel}</span>
            ) : null}
            <span className="membership-pricing-card__helper">
              Choose how long the membership stays active before renewal is needed.
            </span>
            {(errors.validityDays || validityCapError) ? (
              <span className="membership-pricing-card__error">{errors.validityDays || validityCapError}</span>
            ) : null}
          </div>
        </div>

        <label className="field membership-pricing-card__field">
          <span className="label membership-pricing-card__label">Unlock Criteria (optional)</span>
          <textarea
            className="input membership-pricing-card__input membership-pricing-card__textarea"
            value={unlockCriteria}
            onChange={(e) => onChange('unlockCriteria', e.target.value)}
            placeholder="e.g. Spend Rs. 500 in 6 months, Complete 10 visits..."
            maxLength={500}
            rows={3}
          />
          <span className="membership-pricing-card__helper">
            Use plain language to describe how members qualify automatically or manually.
          </span>
          {errors.unlockCriteria ? (
            <span className="membership-pricing-card__error">{errors.unlockCriteria}</span>
          ) : null}
        </label>
      </div>
    </section>
  );
};

export default MembershipTypePricingSection;
